/**
 * Dashboard-tree Telemetry Bootstrap
 *
 * Thin wrapper around the shared core bootstrap (core/telemetry/init): reads
 * this tree's `env` module, passes the derived values down, and triggers the
 * module-level side-effect init that index.tsx relies on.
 *
 * This replaces the legacy hand-rolled WebTracerProvider (tracing/config.ts):
 * the dashboard now gets the same XHR + fetch auto-instrumentation, OTLP
 * export, sanitizers, and LocalStorageExporter wiring as the app tree.
 */

import { DeploymentTypeEnum } from "@superblocksteam/shared";
import type { BrowserTelemetryInstance } from "@superblocksteam/telemetry/browser";

import {
  getTelemetry,
  initTelemetry as initCoreTelemetry,
  resetTelemetry,
} from "core/telemetry/init";

import {
  SUPERBLOCKS_UI_DEPLOYMENT_TYPE,
  SUPERBLOCKS_UI_DP_CONFIG_URL,
  default as envs,
} from "../env";

export { getTelemetry, resetTelemetry };

/**
 * Initialise browser telemetry (idempotent — second calls are no-ops).
 */
export function initTelemetry(): BrowserTelemetryInstance {
  return initCoreTelemetry({
    deploymentType: SUPERBLOCKS_UI_DEPLOYMENT_TYPE || DeploymentTypeEnum.CLOUD,
    serviceVersion: envs.get("SUPERBLOCKS_UI_VERSION"),
    tracingEnv: envs.get("SUPERBLOCKS_UI_TRACING_ENV"),
    dpConfigUrl: SUPERBLOCKS_UI_DP_CONFIG_URL,
  });
}

// Side-effect: initialise on import (matches old config.ts behaviour)
if (typeof window !== "undefined") {
  try {
    initTelemetry();
  } catch (err) {
    console.error(
      "[Telemetry] Failed to initialize — continuing with telemetry disabled",
      err,
    );
  }
}
